import React, { FunctionComponent, useState } from 'react';

import { Game, PaginatedResponse, PromotionGames } from '../../../Games/games.model';
import { PromotionProgressBar } from '../PromotionProgressBar';
import { GameSliderPrompt } from '../../../Games/GameSliderPrompt';
import { Icon } from '../../../shared/Icon';
import { ParsedPromo } from '../types';
import { transformGameData } from '../../../../utils/games/games.utils';
import { useBonusGetBySlugLazyQuery } from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/bonus-get-by-slug.query.generated';
import { locale } from '../../../../consts';
import { Endpoint } from '../../../../models/api.model';
import { LogoSpinner } from '../../../shared/LogoSpinner/LogoSpinner';

export type PromotionBonusBalanceItemProps = {
  promotion: ParsedPromo;
  index: number;
  isFetching: boolean;
  onCloseWindow?: () => void;
};

export const PromotionBonusBalanceItem: FunctionComponent<PromotionBonusBalanceItemProps> = ({
  promotion,
  index,
  isFetching,
  onCloseWindow,
}) => {
  const [isOpenPopUp, setOpenPopUp] = useState<boolean>(false);
  const [gamesByTag, setGamesByTag] = useState<PaginatedResponse<Game>>({
    currentPage: 1,
    itemsPerPage: 8,
    totalPages: 1,
    totalItems: 0,
    items: [],
  });
  const [isLoadingGames, setIsLoadingGames] = useState<boolean>(false);
  const isFreeBet = promotion.isFreeBet;

  // Fetch eligible games for this specific promotion
  const [fetchBonusDetails] = useBonusGetBySlugLazyQuery({
    context: { endpoint: Endpoint.betzoneDirectusAPI },
    fetchPolicy: 'network-only',
  });

  const fetchEligibleGames = async (): Promise<void> => {
    if (promotion.bonusId) {
      setIsLoadingGames(true);
      try {
        const result = await fetchBonusDetails({
          variables: {
            language: locale,
            slug: promotion.pageUrl.replace('/promotions/', ''),
          },
        });

        if (result.data?.bonus[0]?.eligible_games) {
          const eligibleGames = result.data.bonus[0].eligible_games as PromotionGames;
          const transformedGames = eligibleGames
            .map((item) => transformGameData(item?.game_id))
            .filter(Boolean) as Game[];

          setGamesByTag({
            currentPage: 1,
            itemsPerPage: transformedGames.length,
            totalItems: transformedGames.length,
            totalPages: 1,
            items: transformedGames,
          });
        }
      } catch (error) {
        console.error(`Error fetching eligible games for promotion ${promotion.bonusId}:`, error);
      } finally {
        setIsLoadingGames(false);
      }
    }
  };

  const searchGamesByTag = async (index: number): Promise<void> => {
    // Only fetch games if they haven't been fetched yet
    if (gamesByTag.items.length === 0) {
      await fetchEligibleGames();
    }

    setOpenPopUp(!isFreeBet);
  };

  return (
    <>
      {isFetching || isLoadingGames ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <LogoSpinner />
        </div>
      ) : (
        <div
          className="promotion-progress-bar"
          onClick={(): Promise<void> => searchGamesByTag(index)}
          role="button"
          tabIndex={0}
        >
          <PromotionProgressBar promotion={promotion} />
          <span className="promotion-progress-bar__summary-expand">
            <Icon variant={isFreeBet ? 'IoIosFootball' : 'ChevronRight'} />
          </span>
        </div>
      )}
      <GameSliderPrompt
        promotion={promotion}
        gamesByTag={gamesByTag}
        openPopUp={isOpenPopUp}
        setOpenPopUp={setOpenPopUp}
        onCloseWindow={onCloseWindow}
      />
    </>
  );
};
